import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { useAuth } from '@/contexts/AuthContext';
import { isNativePlatform } from '@/utils/capacitor';
import { tryAndroidAppHandoff } from '@/utils/androidAppHandoff';
import { applyDeepLinkNavigation, isOriginOnlyAppUrl, resolveAppDeepLink } from '@/utils/appDeepLink';
import {
  armNativeDeepLinkLaunch,
  clearGuestHomeDeepLinkSuppress,
  markAppDeepLinkLaunchSettled,
  suppressGuestHomeForDeepLink,
} from '@/utils/appDeepLinkLaunch';
import { getAppLaunchHomePath } from '@/utils/redirectByRole';

const LAUNCH_URL_TIMEOUT_MS = 3000;

export function useAppDeepLink() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, auth, isLoading } = useAuth();
  const pendingUrlRef = useRef<string | null>(null);
  const launchConsumedRef = useRef(!isNativePlatform());
  const isLoadingRef = useRef(isLoading);
  const homePathRef = useRef('/');
  const navigateRef = useRef(navigate);

  const homePath = user ? getAppLaunchHomePath(user.role, auth?.slug) : '/';

  isLoadingRef.current = isLoading;
  homePathRef.current = homePath;
  navigateRef.current = navigate;

  if (isNativePlatform()) {
    armNativeDeepLinkLaunch();
  }

  const applyUrl = useCallback((url: string) => {
    if (isOriginOnlyAppUrl(url)) {
      return;
    }
    if (isLoadingRef.current) {
      pendingUrlRef.current = url;
      return;
    }
    const resolved = resolveAppDeepLink(url, homePathRef.current);
    if (resolved !== homePathRef.current) {
      suppressGuestHomeForDeepLink();
    }
    applyDeepLinkNavigation(navigateRef.current, resolved, homePathRef.current);
  }, []);

  const settleLaunchIfReady = useCallback(() => {
    if (!launchConsumedRef.current || isLoadingRef.current) return;
    markAppDeepLinkLaunchSettled();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const pending = pendingUrlRef.current;
    if (pending) {
      pendingUrlRef.current = null;
      applyUrl(pending);
    }
    settleLaunchIfReady();
  }, [isLoading, homePath, applyUrl, settleLaunchIfReady]);

  useEffect(() => {
    if (pathname !== '/') {
      clearGuestHomeDeepLinkSuppress();
    }
  }, [pathname]);

  useEffect(() => {
    tryAndroidAppHandoff();
  }, []);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let cancelled = false;
    let urlHandle: { remove: () => Promise<void> } | undefined;
    const timeoutId = window.setTimeout(() => {
      if (cancelled || launchConsumedRef.current) return;
      launchConsumedRef.current = true;
      settleLaunchIfReady();
    }, LAUNCH_URL_TIMEOUT_MS);

    const consumeLaunchUrl = (url: string | undefined) => {
      if (cancelled) return;
      launchConsumedRef.current = true;
      if (url) {
        applyUrl(url);
      }
      settleLaunchIfReady();
    };

    void App.addListener('appUrlOpen', ({ url }) => {
      applyUrl(url);
    })
      .then(async (handle) => {
        if (cancelled) {
          await handle.remove();
          return;
        }
        urlHandle = handle;
        try {
          const launch = await App.getLaunchUrl();
          consumeLaunchUrl(launch?.url);
        } catch {
          consumeLaunchUrl(undefined);
        }
      })
      .catch(() => {
        consumeLaunchUrl(undefined);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      void urlHandle?.remove();
    };
  }, [applyUrl, settleLaunchIfReady]);
}
